/**
 * useDragDismiss — unified drag-to-dismiss gesture for every drawer,
 * bottom sheet, side panel, and modal in the Link app.
 * ---------------------------------------------------------------------
 *
 * Why:
 *   The product spec calls for "premium native iOS app" feel — every
 *   surface that opens should be dismissable by dragging it away from
 *   its origin (down for bottom sheets, up for top drawers, sideways
 *   for side panels). Spring physics, velocity-aware, scroll-position
 *   aware, rubber-band overscroll, accidental-scroll-safe.
 *
 * Usage (bottom sheet / drawer):
 *
 *   const scrollRef = useRef(null);
 *   const { motionProps, backdropOpacity, isDragging } = useDragDismiss({
 *     onDismiss: () => onOpenChange(false),
 *     origin:    "bottom",     // surface enters from the bottom
 *     scrollRef,               // optional scroll container ref
 *   });
 *
 *   return (
 *     <>
 *       <motion.div style={{ opacity: backdropOpacity }} ... />
 *       <motion.div {...motionProps} className="drawer">
 *         <div ref={scrollRef} className="scroll-area">...</div>
 *       </motion.div>
 *     </>
 *   );
 *
 * Behaviour:
 *   • Following finger    — `motion.div` translates 1:1 with the drag.
 *   • Spring back         — if released BELOW the threshold AND
 *                           BELOW the velocity, animate back to 0.
 *   • Dismiss             — if past threshold OR fast flick, fire
 *                           `onDismiss()`. The caller is responsible
 *                           for unmounting the surface (typically by
 *                           flipping `open` state); AnimatePresence /
 *                           the parent's exit variant handles the exit.
 *   • Rubber band         — dragging the wrong direction is dampened
 *                           1/4 so the surface feels physical.
 *   • Scroll guard        — when a `scrollRef` is provided we only
 *                           begin a dismiss-drag if the scroll
 *                           container is at the very top (for bottom
 *                           sheets) or bottom (for top drawers). This
 *                           prevents users from accidentally
 *                           dismissing while reading content.
 *   • Velocity dismissal  — flicks > 500 px/s dismiss regardless of
 *                           distance.
 */
import { useMotionValue, useTransform, animate } from "framer-motion";
import { useRef, useState, useCallback, useEffect } from "react";

// Tuned to feel like Apple's "lift" — fast initial response, no overshoot.
export const DISMISS_SPRING = { type: "spring", stiffness: 480, damping: 38, mass: 0.4 };
const DISMISS_THRESHOLD_PX = 120;
const DISMISS_VELOCITY_PX_S = 500;
const RUBBER_BAND = 0.25;

const ORIGIN_DIR = {
  // sign === +1 means "dismiss direction is positive". `bottom`-anchored
  // sheets dismiss DOWN (+y). `top`-anchored sheets dismiss UP (-y).
  bottom: { axis: "y", sign:  1 },
  top:    { axis: "y", sign: -1 },
  left:   { axis: "x", sign: -1 },
  right:  { axis: "x", sign:  1 },
};

export function useDragDismiss({
  onDismiss,
  origin = "bottom",
  scrollRef = null,
  enabled = true,
  // When provided, the drag offset auto-resets to 0 every time `open`
  // transitions to false. This is required for morph drawers where the
  // surface persists across open/closed states (the variants animate
  // width/height/top, but our drag adds a separate `y`/`x` transform
  // that must collapse back to 0 or the closed pill will be offset).
  open = undefined,
  thresholdPx = DISMISS_THRESHOLD_PX,
  velocityPxS = DISMISS_VELOCITY_PX_S,
  // Optional: map the drag distance into a backdrop opacity. The
  // backdrop fades out 1 → 0 over `backdropFadeDistance` px of drag.
  backdropFadeDistance = 200,
} = {}) {
  const { axis, sign } = ORIGIN_DIR[origin] || ORIGIN_DIR.bottom;
  const value = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);
  // Track whether the user STARTED the drag from a scroll-locked spot.
  // If the scroll container scrolls AWAY from the dismiss edge mid-
  // drag, we ignore the drag — they're scrolling, not dismissing.
  const scrollLocked = useRef(true);

  const scrollAtDismissEdge = useCallback(() => {
    if (!scrollRef?.current) return true;
    const el = scrollRef.current;
    // Both top- and bottom-anchored drawers gate dismiss to scrollTop===0.
    //   • Bottom sheet (swipe DOWN): user is at the top of content → dismiss.
    //   • Top drawer (swipe UP):     user is at the top of content → dismiss.
    //     Once they scroll down, dismiss is locked so the swipe is a scroll
    //     gesture instead. Scrolling back to the top re-arms dismiss.
    return el.scrollTop <= 1;
  }, [scrollRef]);

  const onDragStart = useCallback(() => {
    if (!enabled) return;
    scrollLocked.current = scrollAtDismissEdge();
    setIsDragging(true);
  }, [enabled, scrollAtDismissEdge]);

  const onDrag = useCallback((_e, info) => {
    if (!enabled) return;
    // If we left the dismiss edge mid-drag, drop the dismiss intent
    // entirely — the user is scrolling content.
    if (!scrollAtDismissEdge()) scrollLocked.current = false;
    if (!scrollLocked.current) { value.set(0); return; }
    const raw = axis === "y" ? info.offset.y : info.offset.x;
    // Dragging IN the dismiss direction → 1:1 follow.
    // Dragging the OTHER way → rubber-band.
    const inDirection = sign * raw > 0;
    value.set(inDirection ? raw : raw * RUBBER_BAND);
  }, [enabled, value, axis, sign, scrollAtDismissEdge]);

  const onDragEnd = useCallback((_e, info) => {
    setIsDragging(false);
    if (!enabled || !scrollLocked.current) {
      animate(value, 0, DISMISS_SPRING);
      return;
    }
    const offset   = axis === "y" ? info.offset.y   : info.offset.x;
    const velocity = axis === "y" ? info.velocity.y : info.velocity.x;
    const past = sign * offset   >= thresholdPx;
    const fast = sign * velocity >= velocityPxS;
    if (past || fast) {
      onDismiss?.();
      // Don't snap back — the parent will unmount us / animate exit.
      // We still keep `value` at its end position for visual continuity
      // until the parent's exit variant takes over.
    } else {
      animate(value, 0, DISMISS_SPRING);
    }
  }, [enabled, axis, sign, thresholdPx, velocityPxS, onDismiss, value]);

  // Backdrop opacity = 1 at value=0, 0 at value=fadeDistance (in
  // the dismiss direction). Other direction → keep at 1.
  const backdropOpacity = useTransform(value, (v) => {
    const d = sign * v;
    if (d <= 0) return 1;
    return Math.max(0, 1 - d / backdropFadeDistance);
  });

  // Snap the motion value back to 0 whenever the surface closes (or is
  // forced shut by the parent without going through onDismiss). Without
  // this, the next time it opens it appears already-offset by the last
  // drag distance.
  useEffect(() => {
    if (open === false) animate(value, 0, DISMISS_SPRING);
  }, [open, value]);

  // Spread these onto the motion.div that should drag. Pair with a
  // child scroll container ref for scroll-position awareness.
  const motionProps = {
    drag: axis,
    // Only allow movement in the dismiss direction; the other direction
    // is rubber-banded through our onDrag handler.
    dragConstraints: { top: 0, bottom: 0, left: 0, right: 0 },
    dragElastic: 0,                    // we own the math; turn off built-in elastic
    dragMomentum: false,               // we own the dismissal physics
    onDragStart,
    onDrag,
    onDragEnd,
    style: { [axis]: value },
  };

  return {
    motionProps,
    value,
    isDragging,
    backdropOpacity,
  };
}
